import {
  CHRW_BUSINESS_LINE_DIMENSION_SEED,
  FINANCE_SEMANTIC_LAYER_REGISTRY_SEED,
  createMetricRegistry,
  type MetricQuerySpec,
} from './metric-registry';
import {
  compileMetricQuerySpec,
  METRIC_QUERY_COMPILER_REFUSAL_CODES,
} from './metric-query-compiler';

export type VarianceQueryValidationInput = Readonly<{
  tenantId: string;
  metricKey: string;
  basisMetricKey: string;
  dimension: string;
  memberKeys: readonly string[];
  periodType: string;
  baselinePeriodKey: string;
  comparisonPeriodKey: string;
  grainRollup: string;
  dimensionMembersByDimension?: Readonly<Record<string, readonly string[]>>;
  asOfPolicy?: MetricQuerySpec['asOfPolicy'];
  maxCells?: number;
}>;

export type VarianceQueryValidationBlock = Readonly<{
  status: 'blocked';
  reasonCode: 'never_blend_violation' | 'dimension_not_declared' | 'invalid_formula_input';
  message: string;
}>;

export function buildVarianceDecompositionMetricQuerySpec(
  input: VarianceQueryValidationInput,
): MetricQuerySpec {
  return {
    metrics: [input.metricKey, input.basisMetricKey],
    groupBy: [input.dimension],
    filters: buildVarianceDimensionFilters(input),
    period: variancePeriod(input),
    asOfPolicy: input.asOfPolicy ?? 'latest_approved',
    grain: { level: [{ dimension: input.dimension, rollup: input.grainRollup }] },
    budget: { maxCells: input.maxCells ?? Math.max(1, input.memberKeys.length) * 4 },
  };
}

export function buildVarianceDecompositionTotalQuerySpec(
  input: VarianceQueryValidationInput,
): MetricQuerySpec {
  return {
    metrics: [input.metricKey],
    groupBy: [],
    filters: [],
    period: variancePeriod(input),
    asOfPolicy: input.asOfPolicy ?? 'latest_approved',
    grain: { level: [{ dimension: input.dimension, rollup: 'enterprise' }] },
    budget: { maxCells: Math.max(2, input.memberKeys.length) },
  };
}

export function validateVarianceSpecCompilation(
  input: VarianceQueryValidationInput,
  spec: MetricQuerySpec,
): VarianceQueryValidationBlock | null {
  const compiled = compileMetricQuerySpec({
    tenantId: input.tenantId,
    spec,
    registry: createMetricRegistry(FINANCE_SEMANTIC_LAYER_REGISTRY_SEED),
    dimensionMembersByDimension: varianceDimensionMembersByDimension(input),
  });
  if (
    compiled.ok ||
    compiled.refusal.code === METRIC_QUERY_COMPILER_REFUSAL_CODES.artifactFamilyRequired
  ) {
    return null;
  }
  switch (compiled.refusal.code) {
    case METRIC_QUERY_COMPILER_REFUSAL_CODES.blendRefused:
      return neverBlendBlock(compiled.refusal.message);
    case METRIC_QUERY_COMPILER_REFUSAL_CODES.grainNotDeclared:
    case METRIC_QUERY_COMPILER_REFUSAL_CODES.unknownDimensionMember:
      return undeclaredDimensionBlock(compiled.refusal.message);
    default:
      return invalidInputBlock(compiled.refusal.message);
  }
}

function buildVarianceDimensionFilters(
  input: VarianceQueryValidationInput,
): MetricQuerySpec['filters'] {
  return input.memberKeys.length > 0
    ? [{ dimension: input.dimension, memberKeys: [...input.memberKeys] }]
    : [];
}

function variancePeriod(input: VarianceQueryValidationInput): MetricQuerySpec['period'] {
  return {
    type: input.periodType,
    from: input.baselinePeriodKey,
    to: input.comparisonPeriodKey,
  };
}

function varianceDimensionMembersByDimension(
  input: VarianceQueryValidationInput,
): Readonly<Record<string, readonly string[]>> {
  const defaultBusinessLineMembers = CHRW_BUSINESS_LINE_DIMENSION_SEED.members.map(
    (member) => member.memberKey,
  );
  return Object.freeze({
    ...(input.dimensionMembersByDimension ?? {}),
    [input.dimension]:
      input.dimensionMembersByDimension?.[input.dimension] ??
      (input.memberKeys.length > 0 ? input.memberKeys : defaultBusinessLineMembers),
  });
}

function neverBlendBlock(message: string): VarianceQueryValidationBlock {
  return {
    status: 'blocked',
    reasonCode: 'never_blend_violation',
    message: `Variance decomposition refused by semantic-layer never-blend: ${message}`,
  };
}

function undeclaredDimensionBlock(message: string): VarianceQueryValidationBlock {
  return {
    status: 'blocked',
    reasonCode: 'dimension_not_declared',
    message:
      `Variance decomposition dimension is not declared for the requested metric grain: ${message}`,
  };
}

function invalidInputBlock(message: string): VarianceQueryValidationBlock {
  return {
    status: 'blocked',
    reasonCode: 'invalid_formula_input',
    message: `Variance decomposition metric query is invalid: ${message}`,
  };
}
